import { useEffect, useMemo, useState } from "react";
import { parseDelimited } from "@/lib/parse-delimited";
import { fetchFile } from "@/lib/download-file";
import { PreviewSkeleton } from "@/components/preview/PreviewSkeleton";
import { TableGrid } from "@/components/preview/TableGrid";

const PREVIEW_CLASS = "min-h-full bg-editor-bg";

interface CsvPreviewProps {
	url: string;
	/** タブ区切り (.tsv) かどうか。 */
	delimiter: "," | "\t";
	/**
	 * 指定すると fetch をスキップし、この文字列を表示する。Source タブでの未保存編集内容を
	 * Preview タブに反映するために使う (呼び出し側が dirty のときだけ渡す想定)。
	 */
	source?: string;
}

/** CSV / TSV を表グリッドとして描画する。 */
export function CsvPreview({ url, delimiter, source }: CsvPreviewProps) {
	const [content, setContent] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (source !== undefined) return;
		let cancelled = false;
		setContent(null);
		setError(null);
		fetchFile(url)
			.then(async (r) => {
				if (!r.ok) throw new Error(`プレビューの取得に失敗しました (${r.status})`);
				const text = await r.text();
				if (!cancelled) setContent(text);
			})
			.catch((err: unknown) => {
				console.error("CsvPreview fetch failed", err);
				if (!cancelled) setError("プレビューを読み込めませんでした。");
			});
		return () => {
			cancelled = true;
		};
	}, [url, source]);

	const effective = source ?? content;
	// パースは内容に依存するため、リサイズ等による再レンダーでは再実行しない。
	const rows = useMemo(
		() => (effective === null ? null : parseDelimited(effective, delimiter)),
		[effective, delimiter],
	);

	if (error)
		return (
			<div className={PREVIEW_CLASS}>
				<p className="m-0 p-4 text-caption text-danger">{error}</p>
			</div>
		);
	if (rows === null)
		return (
			<div className={PREVIEW_CLASS}>
				<PreviewSkeleton />
			</div>
		);

	return (
		<div className={PREVIEW_CLASS} data-testid="csv-preview">
			<TableGrid rows={rows} />
		</div>
	);
}
